import { useState } from 'react';
import {
  Modal,
  Button,
  Stack,
  Group,
  Text,
  Divider,
  Badge,
  ActionIcon,
  Tooltip,
  Select,
  ScrollArea,
  Loader,
  Center,
  Accordion,
} from '@mantine/core';
import { PiTrash, PiPlus } from 'react-icons/pi';
import {
  useUserDetails,
  useAllPermissions,
  useGrantPermission,
  useRevokePermission,
  useRemovePermissionOverride,
} from '@/models/adminUsers';
import type { User, Permission } from '@/types/admin';

interface ExplicitPermissionsModalProps {
  opened: boolean;
  onClose: () => void;
  user: User | null;
}

export function ExplicitPermissionsModal({
  opened,
  onClose,
  user,
}: ExplicitPermissionsModalProps) {
  const [showGrantSelector, setShowGrantSelector] = useState(false);
  const [showRevokeSelector, setShowRevokeSelector] = useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState<
    string | null
  >(null);

  const { data: userDetails, isLoading: userLoading } = useUserDetails(
    user?.id,
  );
  const { data: allPermissionsData, isLoading: permissionsLoading } =
    useAllPermissions();

  const grantMutation = useGrantPermission();
  const revokeMutation = useRevokePermission();
  const removeMutation = useRemovePermissionOverride();

  const handleGrantPermission = () => {
    if (!user || !selectedPermissionId || !allPermissionsData) return;
    const permission = allPermissionsData.permissions.find(
      (p: Permission) => p.id === parseInt(selectedPermissionId),
    );
    if (!permission) return;

    grantMutation.mutate(
      { userId: user.id, permissionName: permission.name },
      {
        onSuccess: () => {
          setSelectedPermissionId(null);
          setShowGrantSelector(false);
        },
      },
    );
  };

  const handleRevokePermission = () => {
    if (!user || !selectedPermissionId || !allPermissionsData) return;
    const permission = allPermissionsData.permissions.find(
      (p: Permission) => p.id === parseInt(selectedPermissionId),
    );
    if (!permission) return;

    revokeMutation.mutate(
      { userId: user.id, permissionName: permission.name },
      {
        onSuccess: () => {
          setSelectedPermissionId(null);
          setShowRevokeSelector(false);
        },
      },
    );
  };

  const handleRemoveOverride = (permissionName: string) => {
    if (!user) return;
    removeMutation.mutate({ userId: user.id, permissionName });
  };

  // Group permissions by resource
  const groupPermissionsByResource = (permissions: Permission[]) => {
    const grouped: Record<string, Permission[]> = {};
    permissions.forEach((perm) => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });
    return grouped;
  };

  const grants = userDetails?.explicitPermissions.grants || [];
  const revokes = userDetails?.explicitPermissions.revokes || [];
  const grantsGrouped = groupPermissionsByResource(grants);
  const revokesGrouped = groupPermissionsByResource(revokes);

  // Available permissions for granting (exclude already granted)
  const availableForGrant =
    allPermissionsData?.permissions.filter(
      (p: Permission) => !grants.some((g) => g.id === p.id),
    ) || [];

  // Available permissions for revoking (exclude already revoked)
  const availableForRevoke =
    allPermissionsData?.permissions.filter(
      (p: Permission) => !revokes.some((r) => r.id === p.id),
    ) || [];

  const isLoading = userLoading || permissionsLoading;
  const isMutating =
    grantMutation.isPending ||
    revokeMutation.isPending ||
    removeMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Manage Explicit Permissions"
      size="lg"
    >
      <Stack gap="md">
        {/* User Info */}
        {user && (
          <div>
            <Text size="sm" fw={500}>
              {user.info.name}
            </Text>
            <Text size="xs" c="dimmed">
              {user.email}
            </Text>
          </div>
        )}

        <Divider />

        {isLoading ? (
          <Center p="xl">
            <Loader size="sm" />
          </Center>
        ) : (
          <ScrollArea style={{ maxHeight: 500 }}>
            <Stack gap="lg">
              {/* Granted Permissions Section */}
              <div>
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text size="sm" fw={600}>
                      Explicitly Granted Permissions
                    </Text>
                    <Text size="xs" c="dimmed">
                      These permissions override role permissions
                    </Text>
                  </div>
                  <Badge variant="light" color="green">
                    {grants.length}
                  </Badge>
                </Group>

                {grants.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No explicit grants
                  </Text>
                ) : (
                  <Accordion variant="contained">
                    {Object.entries(grantsGrouped).map(([resource, perms]) => (
                      <Accordion.Item key={resource} value={resource}>
                        <Accordion.Control>
                          <Group justify="space-between">
                            <Text size="sm" fw={500} tt="capitalize">
                              {resource}
                            </Text>
                            <Badge size="sm" variant="light">
                              {perms.length}
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap="xs">
                            {perms.map((perm) => (
                              <Group key={perm.id} justify="space-between">
                                <div style={{ flex: 1 }}>
                                  <Text size="sm">{perm.name}</Text>
                                  <Text size="xs" c="dimmed">
                                    {perm.description}
                                  </Text>
                                </div>
                                <Tooltip label="Remove Grant">
                                  <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={() =>
                                      handleRemoveOverride(perm.name)
                                    }
                                    disabled={isMutating}
                                  >
                                    <PiTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            ))}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}

                {!showGrantSelector ? (
                  <Button
                    leftSection={<PiPlus />}
                    variant="light"
                    size="xs"
                    mt="sm"
                    onClick={() => setShowGrantSelector(true)}
                    disabled={availableForGrant.length === 0}
                  >
                    Add Grant
                  </Button>
                ) : (
                  <Stack gap="xs" mt="sm">
                    <Select
                      placeholder="Select permission to grant"
                      data={availableForGrant.map((p: Permission) => ({
                        value: String(p.id),
                        label: `${p.resource}:${p.action} - ${p.description}`,
                      }))}
                      value={selectedPermissionId}
                      onChange={setSelectedPermissionId}
                      searchable
                    />
                    <Group gap="xs">
                      <Button
                        size="xs"
                        onClick={handleGrantPermission}
                        loading={grantMutation.isPending}
                        disabled={!selectedPermissionId}
                      >
                        Grant
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => {
                          setShowGrantSelector(false);
                          setSelectedPermissionId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </Group>
                  </Stack>
                )}
              </div>

              <Divider />

              {/* Revoked Permissions Section */}
              <div>
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text size="sm" fw={600}>
                      Explicitly Revoked Permissions
                    </Text>
                    <Text size="xs" c="dimmed">
                      These permissions are blocked even if granted by roles
                    </Text>
                  </div>
                  <Badge variant="light" color="red">
                    {revokes.length}
                  </Badge>
                </Group>

                {revokes.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No explicit revokes
                  </Text>
                ) : (
                  <Accordion variant="contained">
                    {Object.entries(revokesGrouped).map(([resource, perms]) => (
                      <Accordion.Item key={resource} value={resource}>
                        <Accordion.Control>
                          <Group justify="space-between">
                            <Text size="sm" fw={500} tt="capitalize">
                              {resource}
                            </Text>
                            <Badge size="sm" variant="light">
                              {perms.length}
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap="xs">
                            {perms.map((perm) => (
                              <Group key={perm.id} justify="space-between">
                                <div style={{ flex: 1 }}>
                                  <Text size="sm">{perm.name}</Text>
                                  <Text size="xs" c="dimmed">
                                    {perm.description}
                                  </Text>
                                </div>
                                <Tooltip label="Remove Revoke">
                                  <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={() =>
                                      handleRemoveOverride(perm.name)
                                    }
                                    disabled={isMutating}
                                  >
                                    <PiTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            ))}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}

                {!showRevokeSelector ? (
                  <Button
                    leftSection={<PiPlus />}
                    variant="light"
                    size="xs"
                    color="red"
                    mt="sm"
                    onClick={() => setShowRevokeSelector(true)}
                    disabled={availableForRevoke.length === 0}
                  >
                    Add Revoke
                  </Button>
                ) : (
                  <Stack gap="xs" mt="sm">
                    <Select
                      placeholder="Select permission to revoke"
                      data={availableForRevoke.map((p: Permission) => ({
                        value: String(p.id),
                        label: `${p.resource}:${p.action} - ${p.description}`,
                      }))}
                      value={selectedPermissionId}
                      onChange={setSelectedPermissionId}
                      searchable
                    />
                    <Group gap="xs">
                      <Button
                        size="xs"
                        color="red"
                        onClick={handleRevokePermission}
                        loading={revokeMutation.isPending}
                        disabled={!selectedPermissionId}
                      >
                        Revoke
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => {
                          setShowRevokeSelector(false);
                          setSelectedPermissionId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </Group>
                  </Stack>
                )}
              </div>
            </Stack>
          </ScrollArea>
        )}

        {/* Close Button */}
        <Group justify="flex-end" mt="md">
          <Button onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
